// Duplicating a group (PeerGroupDuplicateDialog.vue, and the store's
// duplicatePeerGroup).  There's no clone endpoint, so the copy is built from
// a create, a settings update, one call per set of COGS and one per member.

import { BasePeerGroup, MemberRole } from '@/models';
import { BasePacket } from '@/models/packet';
import { PeerGroupMember } from '@/models/orgStructure';

// The group forms cap names at this length.
const NAME_LIMIT = 250;
const COPY_SUFFIX = ' (copy)';

/** What comes along besides the settings, which are passed in already built. */
export interface DuplicateParts {
  cogs: boolean;
  teachers: boolean;
  students: boolean;
  observers: boolean;
}

/**
 * The settings a copy takes.  Role names that match the org's are null, so
 * the copy keeps following the org.  The Google Classroom sync switch is left
 * out, since the copy is a plain Unrulr group.
 */
export type CopiedSettings = Pick<
  BasePeerGroup,
  'creatorCanPublish' | 'teachersCanPublish' | 'submissionStatusIsInteractive'
> & {
  roleNameStudent: string | null;
  roleNameStudentPlural: string | null;
  roleNameTeacher: string | null;
  roleNameTeacherPlural: string | null;
};

export type DuplicateStep =
  | 'create'
  | 'settings'
  | 'cogs'
  | 'members'
  | 'archive';

export interface DuplicateProgress {
  step: DuplicateStep;
  done: number;
  total: number;
}

export interface DuplicateResult {
  peerGroupId: number;
  settingsFailed: boolean;
  failedPackets: BasePacket[];
  failedMembers: PeerGroupMember[];
  // False when archiving wasn't asked for, or was skipped because something
  // else failed.
  archived: boolean;
  archiveFailed: boolean;
}

/** "Biology (copy)", shortening the name if the suffix would go over. */
export const duplicateName = (name: string): string =>
  `${name.slice(0, NAME_LIMIT - COPY_SUFFIX.length)}${COPY_SUFFIX}`;

/**
 * The members to add to the copy.  Anyone in `alreadyIn` is left out: the API
 * adds a creator who isn't an org admin as a teacher.
 */
export const membersToCopy = (
  members: PeerGroupMember[],
  parts: DuplicateParts,
  alreadyIn: Set<number>
): PeerGroupMember[] =>
  members.filter(m => {
    if (alreadyIn.has(m.userId)) {
      return false;
    }

    switch (m.role) {
      case MemberRole.TEACHER:
        return parts.teachers;
      case MemberRole.STUDENT:
        return parts.students;
      case MemberRole.OBSERVER:
        return parts.observers;
    }
  });

/** Whether anything besides the archive didn't copy. */
export const copyIsIncomplete = (result: DuplicateResult): boolean =>
  result.settingsFailed ||
  result.failedPackets.length > 0 ||
  result.failedMembers.length > 0;
